import { createMiddleware } from "@tanstack/react-start";
import { TaggedError } from "better-result";
import { m } from "#/paraglide/messages";

const SERIALIZED_HEADER = "x-tss-serialized";

/**
 * A server-function request that never reached its handler: the proxy or Node answered with a
 * bare error page (502/504 while the container restarts, 431 on an oversized URL). Left alone,
 * TanStack's fetcher trips over the missing content-type and surfaces "Invariant failed".
 * Status 0: no response at all — the device lost its connection mid-request.
 */
export class ServerUnavailableError extends TaggedError("ServerUnavailableError")<{
  status: number;
  message: string;
}> {
  constructor(args: { status: number }) {
    super({
      ...args,
      message:
        args.status === 0
          ? m["common.error_boundary_connection_lost"]()
          : m["common.error_boundary_server_unavailable"](),
    });
  }
}

/** The user's network dropped the request; nothing on our side to fix, so it is not reported. */
export function isConnectionLost(cause: unknown): boolean {
  return ServerUnavailableError.is(cause) && cause.status === 0;
}

// fetch and body reads reject with a TypeError ("Failed to fetch", "Load failed", …) only when
// the connection dropped; aborts reject with an AbortError and pass through untouched.
async function orConnectionLost<T>(read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (error) {
    if (error instanceof TypeError) throw new ServerUnavailableError({ status: 0 });
    throw error;
  }
}

export async function serverFnFetch(input: RequestInfo | URL, init?: RequestInit) {
  const response = await orConnectionLost(() => fetch(input, init));
  if (!response.ok && !response.headers.has(SERIALIZED_HEADER)) {
    throw new ServerUnavailableError({ status: response.status });
  }
  // Start reads the body after this wrapper returns, so a connection that drops mid-body would
  // escape as a bare TypeError. JSON is read whole anyway; streamed (framed) responses stay as-is.
  if (!response.headers.get("content-type")?.includes("application/json")) return response;
  const body = await orConnectionLost(() => response.arrayBuffer());
  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

export const serverUnavailableMiddleware = createMiddleware({ type: "function" }).client(
  ({ next }) => next({ fetch: serverFnFetch }),
);
